'use strict';

// external imports

// shared imports
import LocalizableError from '~/shared/src/base/LocalizableError';
import metaModel from '~/shared/src/utilities/metaModel';
import {__} from '~/shared/src/utilities/messageCatalog';
import Status from '~/shared/src/mm/Status';
import ItemType from '~/shared/src/mm/ItemType';
import ReferenceType from '~/shared/src/mm/ReferenceType';
import ReferencePropertySet from '~/shared/src/mm/ReferencePropertySet';

// backend imports


// type definitions
export type ReferenceCardinality = '1:1' | '1:n' | 'n:m' | 'n:1';
export type ReferenceSetAttributes = {
    id? : number,
    srcItemTypeId : number,
    destItemTypeId : number,
    referenceTypeId : number,
    statusId? : number,
    origin? : string,
    module? : string,
    cardinality? : ReferenceCardinality,
    description? : string,
    isImported? : boolean,
    uuid? : string,
    modifierUserId? : string,
    modificationTime? : NDate,
    validFromTime? : NDate,
    validToTime? : NDate
};

export type ReferenceSetAttributesCollection = Array<ReferenceSetAttributes>;

export type ReferenceSetAttributesRequired = keyof Pick<ReferenceSetAttributes, 'id' | 'srcItemTypeId' | 'destItemTypeId' | 'referenceTypeId' | 'statusId'>;

export type ReferenceSetAttributesSettable = Pick<ReferenceSetAttributes, 'statusId' | 'origin' | 'module' | 'cardinality' | 'description' | 'isImported'>;


/**
 * The ReferenceSet class.
 */
class ReferenceSet {

    /**
     * The unique Id counter for ReferenceSets
     */
    static uniqueIdCount = -1;


    /*** ATTRIBUTES ********************************************************************************************************************/

    /**
     * The ID of the ReferenceSet
     */
    readonly id : number;

    /**
     * The SrcItemTypeId of the ReferenceSet
     */
    readonly srcItemTypeId : number;

    /**
     * The DestItemTypeId of the ReferenceSet
     */
    readonly destItemTypeId : number;

    /**
     * The ReferenceTypeId of the ReferenceSet
     */
    readonly referenceTypeId : number;

    /**
     * The StatusID of the ReferenceSet
     */
    protected _statusId : number;
    get statusId() : number {
        return this._statusId;
    }

    set statusId( value : number ) {
        this.validateStatusId( value );

        this.handleModified( 'statusId', value );

        this._statusId = value;
    }

    /**
     * The origin of this
     */
    protected _origin : string = 'FSZ';
    get origin() : string {
        return this._origin;
    }

    set origin( value : string ) {
        this.validateOrigin( value );

        this.handleModified( 'origin', value );

        this._origin = value;
    }

    /**
     * The module this belongs to
     * @protected
     */
    protected _module : string = 'base';
    get module() : string {
        return this._origin;
    }

    set module( value : string ) {
        this.validateModule( value );

        this.handleModified( 'module', value );

        this._module = value;
    }

    /**
     * The type of the key of an Item of the ReferenceSet
     */
    protected _cardinality : ReferenceCardinality = 'n:m';
    get cardinality() : ReferenceCardinality {
        return this._cardinality;
    }

    set cardinality( value : ReferenceCardinality ) {
        this.checkStatus();

        this.handleModified( 'cardinality', value );

        this._cardinality = value;
    }

    /**
     * The description of the ReferenceSet
     */
    protected _description : string = '';
    get description() {
        return this._description;
    }

    set description( value : string ) {
        this.checkStatus();

        this.handleModified( 'description', value );

        this._description = value;
    }

    /**
     * Is the ReferenceSet imported from another datasource
     */
    protected _isImported : boolean = false;
    get isImported() {
        return this._isImported;
    }

    set isImported( value : boolean ) {
        this.checkStatus();

        this.handleModified( 'isImported', value );

        this._isImported = value;
    }

    /**
     * The unique spoken id of the ReferenceSet
     */
    readonly uuid : string = '';

    /**
     * The UserID of the last modifier of the ReferenceSet
     */
    readonly modifierUserId : string = '';

    /**
     * The last modification date of the ReferenceSet
     */
    readonly modificationTime : NDate = null;

    /**
     * The valid from date of a History-ReferenceSet
     */
    readonly validFromTime : NDate = null;

    /**
     * The valid to date of a History-ReferenceSet
     */
    readonly validToTime : NDate = null;


    /*** ALIASES ***********************************************************************************************************************/

    get status() : Status {
        return metaModel[ this._schema ].getStatus( this._statusId )!;
    }

    get srcItemType() : ItemType {
        return metaModel[ this._schema ].getItemType( this.srcItemTypeId )!;
    }

    get destItemType() : ItemType {
        return metaModel[ this._schema ].getItemType( this.destItemTypeId )!;
    }

    get referenceType() : ReferenceType {
        return metaModel[ this._schema ].getReferenceType( this.referenceTypeId )!;
    }

    get name() : string {
        return `${this.srcItemType.name} ${__( 'YN.base.common.reference.directionMarker' )} ${this.referenceType.name} ${__( 'YN.base.common.reference.directionMarker' )} ${this.destItemType.name}`;
    }

    get shortName() : string {
        return `${this.srcItemType.name}:${this.referenceType.name}:${this.destItemType.name}`;
    }

    /**
     * The label for this
     */
    get labelMsgId() : string {
        let retVal = metaModel[ this._schema ].getMessage( 'referenceSet', this.id, 'typeLabel' );

        if ( !retVal || retVal === __( retVal ) ) {
            retVal = this.referenceType.name;
        }

        return retVal;
    }

    get label() : string {
        return `${this.srcItemType.label} ${__( 'YN.base.common.reference.directionMarker' )} ${__( this.labelMsgId )} ${__( 'YN.base.common.reference.directionMarker' )} ${this.destItemType.label}`;
    }

    get referenceTypeName() : string {
        return metaModel[ this._schema ].getReferenceType( this.referenceTypeId )!.name;
    }

    get referenceTypeLabel() : string {
        return __( this.labelMsgId );
    }

    /**
     * The propertySets for this
     */
    get propertySets() : ReferencePropertySet[] {
        return metaModel[ this._schema ].getReferencePropertySetsForReferenceSet( this.id );
    }


    /*************************************************************************************************************************************/

    /**
     * The database schema this Item belongs to
     */
    protected _schema : Schema;
    get schema() {
        return this._schema;
    }

    /**
     * Map containing the modified attributes with their initial values
     */
    protected _attributesModified : Map<keyof ReferenceSetAttributesSettable, AttributeValue>;
    get attributesModified() {
        return this._attributesModified;
    }

    /**
     * The constructor.
     *
     * @param attributes                An object containing attribute-value pairs for this
     * @param schema                    The database schema the Item belongs to
     */
    constructor( attributes : ReferenceSetAttributes, schema : Schema = global.schema ) {

        if ( attributes.id && attributes.id < 1 ) {
            throw new LocalizableError( 'YN.base.dataObject.common.attributes.validation.id.invalid' );
        }

        this._schema = schema;

        this.srcItemTypeId = attributes.srcItemTypeId;
        this.destItemTypeId = attributes.destItemTypeId;
        this.referenceTypeId = attributes.referenceTypeId;

        if ( attributes.id ) {
            this.id = attributes.id;
        }
        else {
            this.id = ReferenceSet.uniqueIdCount--;
        }

        if ( attributes.statusId ) {
            this._statusId = attributes.statusId;
        }
        else {
            this._statusId = metaModel[ schema ].getStatus( 'active' )!.id;
        }

        if ( attributes.origin ) {
            this._origin = attributes.origin;
        }

        if ( attributes.module ) {
            this._module = attributes.module;
        }

        if ( attributes.description ) {
            this._description = attributes.description;
        }

        if ( attributes.cardinality ) {
            this._cardinality = attributes.cardinality;
        }

        if ( attributes.isImported !== undefined ) {
            this._isImported = attributes.isImported;
        }

        if ( attributes.uuid ) {
            this.uuid = attributes.uuid;
        }

        if ( attributes.modifierUserId ) {
            this.modifierUserId = attributes.modifierUserId;
        }
        if ( attributes.modificationTime ) {
            this.modificationTime = attributes.modificationTime;
        }

        if ( attributes.validFromTime ) {
            this.validFromTime = attributes.validFromTime;
        }
        if ( attributes.validToTime ) {
            this.validToTime = attributes.validToTime;
        }

        this._attributesModified = new Map();
    }

    /**
     * Returns an object containing the attributes with their values limited by includeAttributes parameter.
     *
     * @param includeAttributes   The state of the attributes to include in result.
     */
    toJSON( includeAttributes : 'all' | 'modified' = 'all' ) : ReferenceSetAttributes {
        // required attributes
        const retVal : ReferenceSetAttributes = {
            id             : this.id,
            srcItemTypeId  : this.srcItemTypeId,
            destItemTypeId : this.destItemTypeId,
            referenceTypeId: this.referenceTypeId
        };

        if ( includeAttributes === 'all' || this._attributesModified.has( 'statusId' ) ) {
            retVal.statusId = this._statusId;
        }

        if ( includeAttributes === 'all' || this._attributesModified.has( 'origin' ) ) {
            retVal.origin = this._origin;
        }

        if ( includeAttributes === 'all' || this._attributesModified.has( 'module' ) ) {
            retVal.module = this._module;
        }

        if ( includeAttributes === 'all' || this._attributesModified.has( 'cardinality' ) ) {
            retVal.cardinality = this._cardinality;
        }

        if ( includeAttributes === 'all' || this._attributesModified.has( 'description' ) ) {
            retVal.description = this._description;
        }

        if ( includeAttributes === 'all' || this._attributesModified.has( 'isImported' ) ) {
            retVal.isImported = this._isImported;
        }

        // not settable attributes
        if ( includeAttributes === 'all' ) {
            retVal.uuid = this.uuid;
            retVal.modifierUserId = this.modifierUserId;

            // only active attributes
            if ( !this.isHistorized() ) {
                retVal.modificationTime = this.modificationTime;
            }

            // only history attributes
            else {
                retVal.validFromTime = this.validFromTime;
                retVal.validToTime = this.validToTime;
            }
        }

        return retVal;
    }

    /**
     * Builds and returns the signature string for this
     */
    toString() : string {
        return `ReferenceSet '${this.name}' (ID: ${this.id})`;
    }

    /**
     * Resets all settable attributes to their initial values.
     */
    reset() {
        if ( this._attributesModified.has( 'statusId' ) ) {
            this._statusId = this._attributesModified.get( 'statusId' ) as number;
        }

        if ( this._attributesModified.has( 'cardinality' ) ) {
            this._cardinality = this._attributesModified.get( 'cardinality' ) as ReferenceCardinality;
        }

        if ( this._attributesModified.has( 'description' ) ) {
            this._description = this._attributesModified.get( 'description' ) as string;
        }

        if ( this._attributesModified.has( 'isImported' ) ) {
            this._isImported = this._attributesModified.get( 'isImported' ) as boolean;
        }

        this._attributesModified = new Map();
    }

    /**
     * Creates and returns a clone of this with given attributes applied.
     *
     * @param [attributes]          An object containing attribute-value pairs to be applied to the clone
     */
    clone( attributes : ReferenceSetAttributesSettable = {} ) : ReferenceSet {
        const clone : ReferenceSet = Object.assign( {}, this );

        // apply data
        if ( attributes.statusId ) {
            clone.statusId = attributes.statusId;
        }

        if ( attributes.description ) {
            clone.description = attributes.description;
        }

        if ( attributes.cardinality ) {
            clone.cardinality = attributes.cardinality;
        }

        if ( attributes.isImported ) {
            clone.isImported = attributes.isImported;
        }

        return clone;
    }

    /**
     * Checks if this is new.
     */
    isNew() : boolean {
        return this.id < 0;
    }

    /**
     * Checks if this is historized.
     */
    isHistorized() : boolean {
        return this.validFromTime !== null;
    }

    /**
     * Checks if this is deleted.
     */
    isDeleted() : boolean {
        return this._statusId === metaModel[ this._schema ].getStatus( 'history' )?.id || this._statusId === metaModel[ this._schema ].getStatus( 'deleted' )?.id;
    }

    /**
     * Checks if this or an EnumProperty or a Property of this is modified.
     */
    isModified() : boolean {
        return this._attributesModified.size > 0;
    }

    /**
     * Checks if the given attribute is modified or not.
     *
     * @param attribute        The attribute to check if it is modified
     */
    isAttributeModified( attribute : keyof ReferenceSetAttributesSettable ) : boolean {
        return this._attributesModified.has( attribute );
    }

    /**
     * Handles the modified attributes list.
     *
     * @param attribute         The attribute
     * @param value             The value for the attribute
     */
    protected handleModified( attribute : keyof ReferenceSetAttributesSettable, value : AttributeValue ) {
        // set as modified
        if ( !this._attributesModified.has( attribute ) && value !== this[ attribute ] ) {
            this._attributesModified.set( attribute, this[ attribute ] );
        }

        // unset as modified
        else if ( this._attributesModified.has( attribute ) && value === this._attributesModified.get( attribute ) ) {
            this._attributesModified.delete( attribute );
        }
    }

    protected checkStatus() {
        if ( this.isDeleted() ) {
            throw new LocalizableError(
                'YN.base.dataObject.referenceSet.attributes.validation.couldNotChangeDeleted'
            );
        }
    }

    /**
     * Validates the given statusId
     */
    protected validateStatusId( statusId : number ) {
        if ( !metaModel[ this._schema ].getStatus( statusId ) ) {
            throw new LocalizableError( 'YN.base.dataObject.common.attribute.validation.statusId.notExists' );
        }

        if (
            (
                statusId === metaModel[ this._schema ].getStatus( 'history' )?.id
                || statusId === metaModel[ this._schema ].getStatus( 'deleted' )?.id
            )
            && (
                this._attributesModified.size > 1
                || ( this._attributesModified.size === 1 && !this._attributesModified.has( 'statusId' ) )
            )
        ) {
            throw new LocalizableError(
                'YN.base.dataObject.item.attributes.validation.couldNotDeleteChanged'
            );
        }
    }

    /**
     * Validates the origin for this
     */
    protected validateOrigin( origin : string ) {
        this.checkStatus();

        if ( !origin.match( /^[A-Z]+$/ ) ) {
            throw new LocalizableError( 'YN.base.dataObject.common.attribute.validation.origin.isNotUppercase' );
        }
    }

    /**
     * Validates the module for this
     */
    protected validateModule( module : string ) {
        this.checkStatus();
    }

}

export default ReferenceSet;
